import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, DollarSign, Calendar, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useStudents } from '@/contexts';
import { format } from 'date-fns';
import { usePaymentStatusTracking } from '@/hooks/usePaymentStatusTracking';
import PaymentStatusHistory from '@/components/payments/PaymentStatusHistory';
import { useActionTracking } from '@/hooks/useActionTracking';

export default function StudentPaymentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { students, getStudent, updateStudent } = useStudents();
  
  const { history, loadHistory, handleStatusChange } = usePaymentStatusTracking(Number(id));
  const { trackStudentAction } = useActionTracking();
  
  const [student, setStudent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProgramIndex, setSelectedProgramIndex] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState('credit-card');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  
  useEffect(() => {
    if (id) {
      const foundStudent = getStudent(Number(id));
      if (foundStudent) {
        setStudent(foundStudent);
      }
      setLoading(false);
    }
  }, [id, students, getStudent]);
  
  useEffect(() => {
    if (id) {
      loadHistory();
    }
  }, [id, loadHistory]);
  
  const handleProcessPayment = async () => {
    if (!student || !student.programs[selectedProgramIndex]) {
      toast.error("Could not process payment. Program not found.");
      return;
    }
    
    try {
      setProcessing(true);
      
      // Mock payment processing delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const currentProgram = student.programs[selectedProgramIndex];
      
      // Track the payment status change
      await handleStatusChange(
        currentProgram.feeStatus,
        'paid',
        currentProgram.fee || 0,
        currentProgram.title,
        `Payment processed via ${paymentMethod}`
      );

      // Also track in admin action log
      trackStudentAction(
        student.id,
        'Administrator',
        'Payment processed',
        {
          programTitle: currentProgram.title,
          paymentMethod: paymentMethod,
          amount: currentProgram.fee || 0,
          previousStatus: currentProgram.feeStatus,
          newStatus: 'paid'
        }
      );

      // Update the program status to paid
      const updatedPrograms = [...student.programs];
      updatedPrograms[selectedProgramIndex] = {
        ...updatedPrograms[selectedProgramIndex],
        feeStatus: 'paid',
        lastPaymentDate: new Date().toISOString()
      };
      
      // Update student record
      const updatedStudent = {
        ...student,
        programs: updatedPrograms
      };
      
      await updateStudent(Number(id), updatedStudent);
      
      setSuccess(true);
      toast.success(`Successfully processed payment for ${student.name}`);
      
      // Reset after showing success
      setTimeout(() => {
        navigate(`/students/${id}`);
      }, 3000);
      
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error("There was an error processing the payment. Please try again.");
    } finally {
      setProcessing(false);
    }
  };
  
  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Processing Payment</h1>
        </div>
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Loading student information...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (!student) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Student Not Found</h1>
          <Button onClick={() => navigate('/students')} variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Students
          </Button>
        </div>
        <div className="text-center p-12">
          <p>Could not find student information. Please try again.</p>
        </div>
      </div>
    );
  }
  
  const selectedProgram = student.programs[selectedProgramIndex];
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Process Payment</h1>
        <Button onClick={() => navigate(`/students/${id}`)} variant="outline">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Student Details
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Student Information</CardTitle>
            <CardDescription>Payment details for {student?.name}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="program">Select Program</Label>
              <Select 
                value={selectedProgramIndex.toString()} 
                onValueChange={(value) => setSelectedProgramIndex(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a program" />
                </SelectTrigger>
                <SelectContent>
                  {student?.programs.map((program: any, index: number) => (
                    <SelectItem key={index} value={index.toString()}>
                      {program.title} ({program.feeStatus === 'paid' ? 'Paid' : 'Pending'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {student?.programs[selectedProgramIndex] && (
              <>
                <div>
                  <Label>Program</Label>
                  <p className="text-sm text-gray-500">{student.programs[selectedProgramIndex].title}</p>
                </div>
                <div>
                  <Label>Fee Amount</Label>
                  <p className="text-xl font-bold">AFN {student.programs[selectedProgramIndex].fee?.toFixed(2) || '0.00'}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <p className={`text-sm ${student.programs[selectedProgramIndex].feeStatus === 'paid' ? 'text-green-600' : 'text-amber-600'}`}>
                    {student.programs[selectedProgramIndex].feeStatus === 'paid' ? 'Paid' : 'Pending Payment'}
                  </p>
                </div>
                <div>
                  <Label>Due Date</Label>
                  <p className="text-sm flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    {student.programs[selectedProgramIndex].nextPaymentDate 
                      ? format(new Date(student.programs[selectedProgramIndex].nextPaymentDate), 'MMMM d, yyyy')
                      : 'Not scheduled'}
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Payment Method</CardTitle>
            <CardDescription>Select a payment method to continue</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!success ? (
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <input 
                    type="radio" 
                    id="credit-card" 
                    name="payment-method" 
                    value="credit-card"
                    checked={paymentMethod === 'credit-card'}
                    onChange={() => setPaymentMethod('credit-card')}
                    className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                  />
                  <Label htmlFor="credit-card" className="flex items-center">
                    <DollarSign className="h-4 w-4 mr-2" /> Credit/Debit Card
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input 
                    type="radio" 
                    id="cash" 
                    name="payment-method" 
                    value="cash"
                    checked={paymentMethod === 'cash'}
                    onChange={() => setPaymentMethod('cash')}
                    className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                  />
                  <Label htmlFor="cash">Cash</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input 
                    type="radio" 
                    id="bank-transfer" 
                    name="payment-method" 
                    value="bank-transfer"
                    checked={paymentMethod === 'bank-transfer'}
                    onChange={() => setPaymentMethod('bank-transfer')}
                    className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                  />
                  <Label htmlFor="bank-transfer">Bank Transfer</Label>
                </div>
                
                {paymentMethod === 'credit-card' && (
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="card-number">Card Number</Label>
                      <Input id="card-number" placeholder="1234 5678 9012 3456" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="expiry">Expiry Date</Label>
                        <Input id="expiry" placeholder="MM/YY" />
                      </div>
                      <div>
                        <Label htmlFor="cvc">CVC</Label>
                        <Input id="cvc" placeholder="123" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="name">Name on Card</Label>
                      <Input id="name" placeholder="John Doe" />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6">
                <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
                <h3 className="text-xl font-bold text-center">Payment Successful!</h3>
                <p className="text-center text-gray-500 mt-2">
                  Payment has been processed successfully for {student?.programs[selectedProgramIndex]?.title}
                </p>
              </div>
            )}
          </CardContent>
          <CardFooter>
            {!success && (
              <Button 
                className="w-full" 
                onClick={handleProcessPayment}
                disabled={processing || student?.programs[selectedProgramIndex]?.feeStatus === 'paid'}
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : student?.programs[selectedProgramIndex]?.feeStatus === 'paid' ? (
                  'Already Paid'
                ) : (
                  `Pay AFN ${student?.programs[selectedProgramIndex]?.fee?.toFixed(2) || '0.00'}`
                )}
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>
      
      <div className="mt-6">
        <PaymentStatusHistory history={history} />
      </div>
    </div>
  );
}
